import React from 'react';
import ReactDOM from 'react-dom/client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { NotificationProvider } from './components/shared/NotificationModal';
import { queryClient } from './config/queryClient';
import { indexedDBPersister } from './config/queryPersistence';
import { registerMutationDefaults } from './config/mutationDefaults';

// Register mutation defaults BEFORE hydrating persisted state.
// This ensures queued offline mutations have a function to call on app restart.
registerMutationDefaults();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: indexedDBPersister,
        maxAge: 24 * 60 * 60 * 1000, // 24 hours
        dehydrateOptions: {
          shouldDehydrateQuery: query => {
            // Don't persist auth-related or failed queries
            const key = query.queryKey[0] as string;
            const skipKeys = ['auth', 'token'];
            if (skipKeys.includes(key)) return false;
            return query.state.status === 'success';
          },
        },
      }}
      onSuccess={() => {
        // After hydrating persisted cache, resume any queued offline mutations
        queryClient.resumePausedMutations().then(() => {
          // After mutations complete, refetch stale queries for fresh server state
          queryClient.invalidateQueries();
        });
      }}
    >
      <NotificationProvider>
        <App />
      </NotificationProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </PersistQueryClientProvider>
  </React.StrictMode>
);
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.info))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.info);
